import React, { FC, MouseEventHandler, forwardRef, RefAttributes, ReactNode } from 'react';
import { showComponent } from '@trip-ux/common';
import { time } from 'uniqid';
import { KEY_CHARS } from '@trip-ux/constants';
import { omit, trim } from 'lodash';

export type AccordionVariants = 'primary-accordion' | 'secondary-accordion';
export type AccordionStatusTypes = 'ok' | 'info' | 'alert' | 'warn';

export type AccordionProps = {
  children?: React.ReactNode;
  id?: string;
  variant?: AccordionVariants;
  title?: string;
  description?: React.ReactNode | string;
  isOpen?: boolean;
  onClick?: MouseEventHandler;
  status?: AccordionStatusTypes;
  statusLabel?: string;
  className?: string;
  name?: string;
  hasError?: boolean;
  headerElement?: unknown;
  titleElement?: React.ReactNode;
  headingTag?: string;
  toggleElement?: string;
};

const getContent = (description: ReactNode, children: ReactNode) => (description ? description : children);

// Note: props are intentionally typed permissively (via `any` in the ref generic) to
// preserve the original untyped functional-component signature. Adding a strict
// props type here would surface pre-existing event-handler type mismatches on the
// caller side (onKeyPress → toggleAccordion is typed as MouseEventHandler on
// AccordionProps, not KeyboardEventHandler). That is out of scope for the BUG-4/5
// forwardRef + statusLabel-leak fix.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ToggleElement = forwardRef<HTMLElement, any>(
  ({ toggleElement, children, statusLabel, ...props }, ref) => {
    if (toggleElement === 'div') {
      return <div {...props} ref={ref as React.Ref<HTMLDivElement>}>{children}</div>;
    } else {
      const { title } = props;
      const toggleElementButtonProps = omit(props, 'aria-labelledby', 'title');
      return (
        <button
          {...toggleElementButtonProps}
          ref={ref as React.Ref<HTMLButtonElement>}
          aria-label={trim(`clickable ${title} accordion ${statusLabel ? statusLabel : ''}`)}>
          {children}
        </button>
      );
    }
  }
);
ToggleElement.displayName = 'ToggleElement';

export const Accordion: FC<AccordionProps & RefAttributes<HTMLButtonElement>> = forwardRef(
  (
    {
      id = time('accordion-id-'),
      description,
      variant = 'primary-accordion',
      title,
      isOpen = false,
      onClick: toggleAccordion,
      children,
      status,
      statusLabel,
      className = '',
      name = time('accordion-id-'),
      hasError,
      headerElement,
      titleElement,
      headingTag = 'h4',
      toggleElement = 'button'
    },
    ref
  ) => {
    const withStatus = status ? `${id}-status` : '';
    const showTitle = title && !titleElement;
    const showToggle = showTitle && toggleElement === 'button';
    const showButtonToggle = showTitle && toggleElement !== 'button';
    const heading = React.createElement(headingTag, { id: `${id}-header`, className: 'accordion-title' }, title);
    return (
      <div
        id={id}
        data-testid={id || name}
        className={`accordion white-bg ${variant} ${hasError ? 'error' : ''} ${className}`}>
        <ToggleElement
          toggleElement={toggleElement}
          id={`${id}-toggle-button`}
          data-testid={`${id}-toggle-button`}
          title={title}
          statusLabel={statusLabel}
          tabIndex={0}
          onKeyPress={event => {
            if (event.key === KEY_CHARS.ENTER || event.key === KEY_CHARS.SPACE) {
              event.preventDefault();
              toggleAccordion(event);
            }
          }}
          onClick={e => {
            e.preventDefault();
            toggleAccordion(e);
          }}
          ref={ref}
          className="accordion-button">
          {showComponent(
            showToggle,
            <>
              <span aria-hidden="true" className={`${isOpen ? 'accordion-close' : 'accordion-open'} my-auto`} />
              {heading}
            </>
          )}
          {showComponent(
            showButtonToggle,
            <>
              <button
                data-testid="button-toggle"
                className="icon"
                aria-expanded={isOpen}
                aria-label={`${title} accordion`}
                aria-labelledby={`${id}-header ${withStatus}`}
                tabIndex={-1}>
                <span aria-hidden="true" className={`${isOpen ? 'accordion-close' : 'accordion-open'} my-auto`} />
              </button>
              {heading}
            </>
          )}
          {showComponent(
            status,
            <span id={`${id}-status`} className={`status my-auto ${status}`}>
              {statusLabel}
            </span>
          )}
          {showComponent(titleElement, <>{titleElement}</>)}
        </ToggleElement>
        {showComponent(headerElement, <>{headerElement}</>)}
        {showComponent(
          isOpen,
          <div id={`${id}-description`} data-testid={`${id}-description`} className="accordion-description">
            {getContent(description, children)}
          </div>
        )}
      </div>
    );
  }
);

Accordion.displayName = 'Accordion';
export default Accordion;
